// The horizontal "common devices" row that sits above the room list on the
// Rooms screen and above the device grid on the Devices screen (identical code
// in roomsScreen.dart and devicesScreen.dart).
//
// PINNED, NOT SCROLLED AWAY. Both screens place this as a fixed row above their
// scrolling list — the Dart has it as a plain child of a Column with the list in
// an Expanded below, so it never leaves the screen. It owns its own horizontal
// scroll; the list below owns the vertical one. Putting it inside the vertical
// scroller (as an earlier version did) makes common devices disappear as soon as
// you scroll the rooms, which is exactly what they must not do.
//
// flexShrink: 0 matters — without it the flex column squeezes this row to
// nothing when the list below is long.
//
// ── Card size ──────────────────────────────────────────────────────────────
// The Dart used width*0.3 clamped 110–200 (image width*0.1 clamped 40–70), and
// this row is a SECONDARY one — it sits above the thing the user came for, and
// on a phone it was taking a third of the width per card for a picture and a
// name. Shrunk deliberately: more of the room's own devices are visible without
// scrolling, and one more common device fits on screen.
//
// ⚠️ This is now the size for the SENSORS strip too. They are two rows of the
// same kind of thing, stacked, and a size difference between them reads as a
// mistake rather than as a distinction.
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import DeviceCard from './DeviceCard';

export const STRIP_CARD_SIZE = (vw) => Math.min(150, Math.max(88, vw * 0.22));
export const STRIP_IMAGE_SIZE = (vw) => Math.min(52, Math.max(30, vw * 0.075));
export const STRIP_LABEL_FONT = 12;

export default function CommonDevicesStrip({
  devices, onOpen, onToggleFavourite, hiddenCount = 0,
}) {
  const theme = useTheme();
  const c = theme.sli;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const cardSize = STRIP_CARD_SIZE(vw);
  const imgSize = STRIP_IMAGE_SIZE(vw);

  /*
   * ⚠️ SAY WHEN THE FILTER TOOK THEM, never just show fewer. "No Common Devices
   * Found" printed over a list the checkbox emptied is how two working current
   * sensors at Factory got reported as missing devices — the message named the
   * wrong problem and sent someone to check the hardware.
   */
  const hiddenNote = hiddenCount > 0 ? (
    <Typography sx={{ color: c.text, opacity: 0.65, fontSize: 11, px: 2, pt: 0.5 }}>
      {`${hiddenCount} offline ${hiddenCount > 1 ? 'devices are' : 'device is'} hidden`}
    </Typography>
  ) : null;

  if (!devices || devices.length === 0) {
    return (
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Typography sx={{ color: c.text, fontSize: 14 }}>
          {hiddenCount > 0
            ? `All ${hiddenCount} common device${hiddenCount > 1 ? 's are' : ' is'} offline and hidden`
            : 'No Common Devices Found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      {hiddenNote}
      {/* data-hscroll: a real horizontal scroller — the tab-swipe handler leaves
          gestures that start in here alone (see useSwipeNav). */}
      <Box
        data-hscroll
        sx={{
          display: 'flex',
          overflowX: 'auto',
          px: 1,
          py: 1,
          gap: 1.25,
        }}
      >
        {devices.map((d) => (
          <Box key={d.UniqueNumber} sx={{ width: cardSize, height: cardSize, flexShrink: 0 }}>
            <DeviceCard
              device={d}
              imageSize={imgSize}
              nameFontSize={STRIP_LABEL_FONT}
              onOpen={onOpen}
              onToggleFavourite={onToggleFavourite}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
